import { getPrincipleInterest } from '@elevationscu/common-mortgage-calculator';
import { findProperty } from '../services/findProperty';

export function calculateEstimatedMonthlyPayment(principalInterest, taxes, propertyInsurance, floodInsurance, mortgageInsurance) {
  return Math.round((principalInterest || 0) + (taxes || 0) + (propertyInsurance || 0) + (floodInsurance || 0) + (mortgageInsurance || 0));
}

export const isDifferenceGreaterThan30Days = (saleDate, docDate) => {
  const saleDateObj = new Date(saleDate);
  const docDateObj = new Date(docDate);
  const timeDifferenceMs = docDateObj - saleDateObj;
  const timeDifferenceDays = timeDifferenceMs / (1000 * 60 * 60 * 24);
  return timeDifferenceDays > 30;
};

export const handleAddressChange = async (inputAddress) => {
  const response = await findProperty(inputAddress, 5);
  return response?.data || [];
};

export const monthDiff = (startDate, endDate) => {
  let months = ((endDate.getFullYear() - startDate.getFullYear()) * 12) + (endDate.getMonth() - startDate.getMonth());
  if (endDate.getDate() < startDate.getDate()) months -= 1;
  return Math.max(0, months);
};

export const fixNaN = (value) => {
  if (Number.isNaN(value) || value === undefined) {
    // eslint-disable-next-line no-param-reassign
    value = 0;
  }
  return value;
};

export const calculateTotalCurrentMortgageAmount = (currentLoanAssumptionsData) => {
  let mortgageAmount = parseFloat(currentLoanAssumptionsData?.estimatedMortageBalance) || 0;

  if (currentLoanAssumptionsData?.includeSecondLoan === 'Yes') {
    mortgageAmount += parseFloat(currentLoanAssumptionsData?.estimatedSecondMortgageBalance) || 0;
  }

  // setCurrentMortgageAmount(mortgageAmount);
  return mortgageAmount;
};

export const isSecondLoanRolledIn = (loanAssumption) => {
  const firstLoanAmount = Number(loanAssumption?.estimatedMortageBalance) || 0;
  return calculateTotalCurrentMortgageAmount(loanAssumption) > firstLoanAmount;
};

export const roundUpToNearestEighth = (interestRate) => {
  const integerPart = Math.floor(interestRate);
  const decimalPart = interestRate - integerPart;

  // This will round up the decimal to the nearest 1/8th and then add it to the integer
  const scaledDecimal = decimalPart * 8;
  const roundedScaledDecimal = Math.ceil(scaledDecimal);
  const roundedDecimal = roundedScaledDecimal / 8;

  return integerPart + roundedDecimal;
};

export const formatUSD = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(value);

export const getDefaultCards = (reasonForRefinanceLowerCase, refiReason, loanType) => {
  const normalizedReason = refiReason?.toLowerCase();
  const isFHA = loanType?.includes('FHA');
  const isVA = loanType?.includes('VA');
  let cards = [];

  if (
    normalizedReason.includes(reasonForRefinanceLowerCase.LOWER_PAYMENT)
    || normalizedReason.includes(reasonForRefinanceLowerCase.LOWER_RATE)
  ) {
    if (isFHA) {
      cards = ['30 year fixed FHA Streamlined', '30 year fixed FHA', '30 year fixed'];
    } else if (isVA) {
      cards = ['30 year fixed VA IRRL', '30 year fixed VA', '30 year fixed'];
    } else {
      cards = ['30 year fixed', '7 year ARM', '5 year ARM'];
    }
  }

  if (normalizedReason.includes(reasonForRefinanceLowerCase.CONSOLIDATE)) {
    if (isFHA) {
      cards = ['30 year fixed FHA', '30 year fixed', 'HELOAN'];
    } else if (isVA) {
      cards = ['30 year fixed VA', '30 year fixed', 'HELOAN'];
    } else {
      cards = ['30 year fixed', 'HELOAN', 'HELOC'];
    }
  }

  if (normalizedReason.includes(reasonForRefinanceLowerCase.CASH_OUT)) {
    if (isFHA) {
      cards = ['30 year fixed FHA', '30 year fixed', 'HELOC'];
    } else if (isVA) {
      cards = ['30 year fixed VA', '30 year fixed', 'HELOC'];
    } else {
      cards = ['HELOAN', 'HELOC', '30 year fixed'];
    }
  }

  if (normalizedReason.includes(reasonForRefinanceLowerCase.PAY_OFF)) {
    if (isFHA) {
      cards = ['15 year fixed FHA', '20 year fixed FHA', '15 year fixed'];
    } else if (isVA) {
      cards = ['15 year fixed VA', '20 year fixed VA', '15 year fixed'];
    } else {
      cards = ['15 year fixed', '20 year fixed', '30 year fixed'];
    }
  }

  return cards;
};

export const isStreamlinedOrIRRL = (loanName) => {
  const normalized = loanName?.toLowerCase();
  return normalized?.includes('streamlined') || normalized?.includes('irrl');
};

export const isVaAppraisal = (loanName) => {
  const normalized = loanName?.toLowerCase();
  return normalized?.includes('va');
};

export const isFHAStreamlined = (loanName) => {
  const normalized = loanName?.toLowerCase();
  return normalized?.includes('streamlined');
};

export const calculateFHAStreamlinedAmount = (currentBalance, thirtyDaysInterest) => {
  const numBalance = parseFloat(currentBalance) || 0;
  const numInterest = parseFloat(thirtyDaysInterest) || 0;
  return numBalance + numInterest;
};

export const calculateMIPByCardType = (cardName, balance, thirtyDaysInterest) => {
  const numBalance = parseFloat(balance) || 0;
  const numInterest = parseFloat(thirtyDaysInterest) || 0;
  const normalized = cardName?.toLowerCase() ?? '';
  if (normalized.includes('streamlined')) {
    return (numBalance + numInterest) * 0.0175;
  }
  if (normalized.includes('fha')) {
    return numBalance * 0.0175;
  }
  if (normalized.includes('irrl')) {
    return numBalance * 0.005;
  }
  if (normalized.includes('va')) {
    return numBalance * 0.033;
  }
  return 0;
};

export const calculateMIPRefund = (originalLoanAmount, docDate) => {
  if (!docDate || !originalLoanAmount) return 0;
  const months = monthDiff(new Date(docDate), new Date());
  if (months < 1 || months > 36) return 0;
  const refundPercent = (82 - months * 2) / 100;
  const originalMIP = parseFloat(originalLoanAmount) * 0.0175;
  return originalMIP * refundPercent;
};

export const normalizeList = (list) => Object.fromEntries(
  Object.entries(list).map(([key, value]) => [key, value.toLowerCase()]),
);

export const getFutureMonthYear = (monthsToAdd, fromDate = new Date()) => {
  const date = new Date(fromDate);
  date.setMonth(date.getMonth() + monthsToAdd);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${year}`;
};

export function isARM(loan) {
  return loan?.includes('ARM');
}

export function isHeloc(loan) {
  return loan === 'HELOC';
}

export function hasHeloc(loan) {
  return loan?.includes('HELOC');
}

export function isHeloan(loan) {
  return loan === 'HELOAN';
}

export function hasHeloan(loan) {
  return loan?.includes('HELOAN');
}

export function isHeloan3020(loan) {
  return loan === 'HELOAN 30/20';
}

// Derives the current monthly payment for a kept second loan from its stored rate, balance, term, and type.
// LO-created proposals persist the second loan without this payment, so it is computed when the proposal loads.
export const calculateSecondLoanMonthlyPayment = (loanAssumption) => {
  const currentBalance = parseFloat(loanAssumption?.estimatedSecondMortgageBalance) || 0;
  const interestRate = parseFloat(loanAssumption?.estimatedSecondInterestRate) || 0;
  if (currentBalance <= 0 || interestRate <= 0) {
    return 0;
  }
  if (hasHeloc(loanAssumption?.estimatedSecondCurrentLoanType)) {
    return fixNaN(currentBalance * ((interestRate / 100) / 12));
  }
  const secondLoanTerm = parseFloat(loanAssumption?.secondLoanTerm) || 0;
  const originalLoanAmount = parseFloat(loanAssumption?.estimatedSecondCurrentLoanAmount) || 0;
  return fixNaN(getPrincipleInterest(interestRate / 100, secondLoanTerm, originalLoanAmount, currentBalance));
};

export function buildMoreInfoDataPoints({
  closingCostsData,
  selectedCardId,
  cardMap,
  totalLoanAmounts,
  cardName,
  loanAssumptions,
  ficoInfo,
}) {
  const selectedCardData = cardMap?.[selectedCardId];
  const cardIndex = { card1: 0, card2: 1, card3: 2 };
  const totalLoanAmount = totalLoanAmounts?.[cardIndex[selectedCardId]];

  const loanTotal = totalLoanAmount;
  const cashToClosingText = selectedCardData?.cashDueOrRefundAtClosing === 'due' ? 'CASH TO CLOSING' : 'CASH RECEIVED';
  const cashToClosing = Math.abs(selectedCardData?.cashAtClosing ?? 0);

  const baseClosingCosts = closingCostsData?.fundsAtClosing?.closingCosts ?? 0;
  const discountOrRebate = selectedCardData?.discountOrRebate ?? 0;

  let closingCostsRaw = baseClosingCosts;
  if (selectedCardData?.hasDiscount === true) {
    closingCostsRaw = baseClosingCosts + discountOrRebate;
  } else if (selectedCardData?.hasDiscount === false) {
    closingCostsRaw = baseClosingCosts - discountOrRebate;
  }

  const balance = parseFloat(loanAssumptions?.estimatedMortageBalance) || 0;
  const thirtyDaysInterest = closingCostsData?.estimatedPrepaidExpenses?.interimInterestAmount ?? 0;
  const mip = calculateMIPByCardType(cardName, balance, thirtyDaysInterest);
  const mipRefund = calculateMIPRefund(loanAssumptions?.originalLoanVal, ficoInfo?.docDate);
  closingCostsRaw += Math.round(mip);
  if (isStreamlinedOrIRRL(cardName)) {
    closingCostsRaw -= Math.round(closingCostsData?.estimatedClosingCosts?.appraisalFee ?? 0);
  } else if (isVaAppraisal(cardName) && !isStreamlinedOrIRRL(cardName)) {
    closingCostsRaw += 150; // This makes the closing costs 800
  }

  const monthlyPaymentSavings = selectedCardData?.paymentChange || 1;
  const termInMonths = selectedCardData?.termInMonths;
  const totalInterest = selectedCardData?.totalInterest;

  let breakeven = 'N/A';
  const isSavings = selectedCardData?.paymentPosNeg === '-';

  if (isSavings && closingCostsRaw != null) {
    const closingCosts = Number(closingCostsRaw);
    const paymentSavings = Number(monthlyPaymentSavings) || 1;

    breakeven = paymentSavings > 0 ? `${Math.ceil(closingCosts / paymentSavings)} months` : 'N/A';
  }

  // Total interest is unknown for ARM and HELOAN 30/20
  const totalInterestVariable = isARM(selectedCardData.selectedLoan) || isHeloan3020(selectedCardData.selectedLoan);

  // ARM matures in 30 years like a fixed loan
  let maturationTermInMonths = termInMonths;
  if (isHeloan3020(selectedCardData.selectedLoan)) {
    maturationTermInMonths = 0;
  }

  const totalInterestFormatted = (!totalInterestVariable && termInMonths > 0) ? formatUSD(totalInterest) : 'N/A';
  const maturationDate = maturationTermInMonths > 0 ? getFutureMonthYear(maturationTermInMonths) : 'N/A';
  const remainingMonthsFormatted = termInMonths > 0 ? String(termInMonths) : 'N/A';
  const loanTotalFormatted = formatUSD(loanTotal);
  const cashToClosingFormatted = formatUSD(cashToClosing);

  // Combined LTV always includes both loans, regardless of the Include toggle.
  const homeValue = Number(loanAssumptions?.estimatedHomeValue) || 0;
  const secondBalance = Number(loanAssumptions?.estimatedSecondMortgageBalance) || 0;
  const newLoanAmount = Number(totalLoanAmount) || 0;
  const combinedLoanToValue = homeValue > 0
    ? `${(((newLoanAmount + secondBalance) / homeValue) * 100).toFixed(2)}%`
    : '—';

  return {
    loanTotal: loanTotalFormatted,
    cashToClosing: cashToClosingFormatted,
    closingCosts: formatUSD(closingCostsRaw),
    breakevenIn: breakeven,
    totalInterest: totalInterestFormatted,
    maturationDate,
    remainingMonths: remainingMonthsFormatted,
    combinedLoanToValue,
    cashToClosingText,
    mip,
    mipRefund,
  };
}

export function buildCurrentLoanData(currentLoanAssumptions) {
  if (!currentLoanAssumptions) {
    return undefined;
  }
  const balance = Number(currentLoanAssumptions.estimatedMortageBalance) || 0;
  const homeValue = Number(currentLoanAssumptions.estimatedHomeValue) || 0;
  const rate = Number(currentLoanAssumptions.estimatedInterestRate) || 0;
  const remainingMonths = Number(currentLoanAssumptions.estimatedRemainingMonths) || 0;
  const monthlyPayment = Number(currentLoanAssumptions.estimatedMonthlyPayment) || 0;
  const remainingInterest = Number(currentLoanAssumptions.estimatedRemainingInterest) || 0;

  const today = new Date();
  const maturity = new Date(today.getFullYear(), today.getMonth() + remainingMonths, 1);
  const maturityDate = `${String(maturity.getMonth() + 1).padStart(2, '0')}/${maturity.getFullYear()}`;

  // Include 2nd loan only when it's being rolled into the refi (Include = Yes).
  const totalBalance = calculateTotalCurrentMortgageAmount(currentLoanAssumptions);

  // Combined LTV always includes both loans, regardless of the Include toggle.
  const secondBalance = Number(currentLoanAssumptions.estimatedSecondMortgageBalance) || 0;
  const loanToValue = homeValue > 0 ? `${((balance / homeValue) * 100).toFixed(2)}%` : '—';
  const combinedLoanToValue = homeValue > 0
    ? `${(((balance + secondBalance) / homeValue) * 100).toFixed(2)}%`
    : '—';

  return {
    totalLoanAmount: formatUSD(totalBalance),
    remainingMonths: String(remainingMonths),
    payment: formatUSD(monthlyPayment),
    interestRateApr: `${rate.toFixed(3)}%`,
    cashDueAtClosing: '$0',
    loanToValue,
    combinedLoanToValue,
    remainingInterest: formatUSD(remainingInterest),
    closingCosts: '$0',
    maturityDate,
  };
}

export const getMathSymbol = (number) => (number >= 0 ? '+' : '-');

export function getTermInMonths(termText) {
  if (!termText) {
    return 0;
  }

  const normalized = termText?.trim().toUpperCase();

  if (isHeloan(normalized)) {
    return 240;
  }

  if (isHeloan3020(normalized)) {
    return 360;
  }

  if (isHeloc(normalized)) {
    return 0;
  }

  if (isARM(normalized)) {
    return 360;
  }

  // For values like "30 year fixed", "20 year fixed", etc.
  const match = normalized.match(/^(\d+)\s+YEAR/);
  if (match && match[1]) {
    return parseInt(match[1], 10) * 12;
  }
  return 0;
}

// Term shown on the card. HELOAN 30/20 matures in 20 years even though its payment amortizes over 30.
export function getDisplayTermInMonths(termText) {
  if (isHeloan3020(termText?.trim().toUpperCase())) {
    return 240;
  }
  return getTermInMonths(termText);
}

export function getBandLoanType(currentLoanType, band) {
  if (!currentLoanType) {
    return null;
  }
  const normalized = currentLoanType.trim();
  if (isARM(normalized)) {
    return null;
  }
  const bandYears = { ThirtyYear: '30', TwentyYear: '20', FifteenYear: '15' };
  const years = bandYears[band];
  if (!years) {
    return null;
  }
  const rest = normalized.replace(/^\d+\s+/, '');
  if (rest === normalized) {
    return null;
  }
  const newType = `${years} ${rest}`;
  return newType === normalized ? null : newType;
}

export function getHighlightedOption(reason) {
  if (!reason) return '';
  const normalized = reason.trim().toLowerCase();
  if (normalized === 'lower my payment' || normalized === 'consolidate debt' || normalized === 'take cash out') {
    return 'payment';
  }
  if (normalized === 'lower my rate') {
    return 'interestRate';
  }
  if (normalized === 'pay off mortgage faster') {
    return 'totalInt';
  }
  return '';
}

export const getDate = () => {
  const today = new Date();
  const month = today.getMonth() + 1; // Months are zero-based
  const day = today.getDate();
  const year = today.getFullYear();
  return `${month}/${day}/${year}`;
};

export function hasVA(loan) {
  return loan?.includes('VA');
}

export function hasFHA(loan) {
  return loan?.includes('FHA');
}

export function isHomeEquity(loan) {
  return isHeloan(loan) || isHeloc(loan) || isHeloan3020(loan);
}

function validateHomeEquity(loan) {
  return isHomeEquity(loan) ? loan : '';
}

export function getHomeEquityValues(loanOne, loanTwo, loanThree) {
  const normalizedLoanOne = loanOne?.toUpperCase();
  const normalizedLoanTwo = loanTwo?.toUpperCase();
  const normalizedLoanThree = loanThree?.toUpperCase();

  const result = [];
  let data = validateHomeEquity(normalizedLoanOne);
  if (data !== '') result.push(data);
  data = validateHomeEquity(normalizedLoanTwo);
  if (data !== '') result.push(data);
  data = validateHomeEquity(normalizedLoanThree);
  if (data !== '') result.push(data);

  return [...new Set(result)]; // to return unique values
}

export function getApplicantSecureId() {
  const id = window.location.hash.replace(/^#\/?/, '').split('?')[0];
  return id || null;
}

export const isValidApplicationData = (data) => {
  if (!data || typeof data !== 'object') return false;

  const requiredFields = [
    'estimatedHomeValue',
    'mortgages',
    'cashRequest',
    'county',
    'address',
    'loanTerm',
    'dueDate',
    'user_credit',
    'primary_reason',
    'salePrice',
    'salesDate',
    'loanType',
    'docDate',
    'property_id',
  ];

  const hasMissingField = requiredFields.some(
    (field) => data[field] === undefined || data[field] === null,
  );

  if (hasMissingField) {
    return false;
  }

  if (!Array.isArray(data.mortgages) || data.mortgages.length === 0) return false;

  return true;
};

export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') return '';
  const trimmed = email.trim();
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(trimmed) ? trimmed : '';
};

export const sanitizePhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  return digits;
};

export const hasSecondLoanOnly = (loans) => loans?.[0]?.type === '2nd-HELOC' || loans?.[0]?.type === '2nd';

/**
 * LTV Thresholds for determining when to call Optimal Blue
 *
 * Conventional (Fixed and ARM) - No Cash Out:
 *   <=30, 30-60, 60-70, 70-75, 75-80, 80-85, 85-90, 90-95, >95
 *
 * Conventional (Fixed and ARM) - Cash Out:
 *   <=30, 30-60, 60-70, 70-75, 75-80, >80
 *
 * FHA and VA:
 *   No thresholds (do not call OB for LTV changes)
 */
export const getLTVThresholdBand = (ltv, isCashOut = false) => {
  if (isCashOut) {
    if (ltv <= 30) return 'cash-out-0-30';
    if (ltv <= 60) return 'cash-out-30-60';
    if (ltv <= 70) return 'cash-out-60-70';
    if (ltv <= 75) return 'cash-out-70-75';
    if (ltv <= 80) return 'cash-out-75-80';
    return 'cash-out-80+';
  }

  if (ltv <= 30) return '0-30';
  if (ltv <= 60) return '30-60';
  if (ltv <= 70) return '60-70';
  if (ltv <= 75) return '70-75';
  if (ltv <= 80) return '75-80';
  if (ltv <= 85) return '80-85';
  if (ltv <= 90) return '85-90';
  if (ltv <= 95) return '90-95';
  return '95+';
};

/**
 * Term Thresholds for determining when to call Optimal Blue.
 *
 * A custom loan term only changes the rate when it crosses into a different
 * standard product band, so OB is only called when the band changes:
 *   > 240 months  -> ThirtyYear
 *   181-240 months -> TwentyYear
 *   <= 180 months -> FifteenYear
 */
export const getTermThresholdBand = (months) => {
  const totalMonths = Number(months) || 0;
  if (totalMonths > 240) {
    return 'ThirtyYear';
  }
  if (totalMonths > 180) {
    return 'TwentyYear';
  }
  return 'FifteenYear';
};

/**
 * Determines if OB should be called based on LTV threshold crossing
 */
export const shouldCallOB = (previousLTV, newLTV, loanType, isCashOut = false, cardName = '') => {
  if (isStreamlinedOrIRRL(cardName)) {
    return true;
  }

  const isFHA = loanType?.includes('FHA');
  const isVA = loanType?.includes('VA');

  // FHA and VA loans: no thresholds, don't call OB for LTV changes
  if (isFHA || isVA) {
    return false;
  }

  // Conventional loans: check if crossed threshold
  const previousBand = getLTVThresholdBand(previousLTV, isCashOut);
  const newBand = getLTVThresholdBand(newLTV, isCashOut);

  return previousBand !== newBand;
};

/**
 * Calculates LTV percentage from loan amount and property value
 */
export const calculateLTV = (loanAmount, propertyValue) => {
  if (!propertyValue || propertyValue === 0) return 0;
  return Number((loanAmount / propertyValue) * 100).toFixed(2);
};

/**
 * Error-checking logic for OB/rate API responses.
 */
export const checkErrorResponse = (response) => {
  if (response.message === 'ERROR') {
    return { hasError: true, errorMessage: 'Connection error. Please refresh the page and try again.' };
  }
  if (response.message) {
    return { hasError: true, errorMessage: 'The selected loan is unavailable. There are a few reasons this may happen, including but not limited to: high Loan to Value or Jumbo loan restrictions. Try changing the total loan amount or call us at 800.460.2889 to discuss.' };
  }
  return { hasError: false, errorMessage: undefined };
};

/**
 * Maps and deduplicates rate options from an OB result.
 */
export const mapRateOptions = (allRateOptions, selectedRateIndex) => {
  const mappedRates = allRateOptions?.map((loan) => ({
    label: loan.selectedLoan,
    value: loan,
  }));

  const uniqueMappedRates = mappedRates?.filter((loan, idx, self) => idx === self.findIndex((l) => l.value.productId === loan.value.productId
    && l.value.interestRate === loan.value.interestRate
    && l.value.apr === loan.value.apr
    && l.value.points === loan.value.points
    && l.value.discount === loan.value.discount));

  return { rates: uniqueMappedRates, selectedRateIndex };
};

export const getDisplayCardData = (cardData) => ({
  ...cardData,
  cashAtClosing: Math.abs(cardData.cashAtClosing),
});

/**
 * Shared formula for minCash and onePayment buttons.
 * Returns the loan amount needed to achieve the target cash at closing given a rate price.
 * - buttonType 'minCash': target is $0 cash at closing
 * - buttonType 'onePayment': target is -currentMonthlyPayment cash at closing
 */
export const calculateTargetLoanAmount = (buttonType, activeClosingCosts, loanAssumptions, ratePrice) => {
  const currentLoanBalance = Number(loanAssumptions?.estimatedMortageBalance) || 0;
  const secondLoanBalance = Number(activeClosingCosts?.fundsAtClosing?.secondLoan) || 0;
  const prepaidExpenses = Number(activeClosingCosts?.fundsAtClosing?.prepaidExpenses) || 0;
  const baseClosingCosts = Number(activeClosingCosts?.fundsAtClosing?.closingCosts) || 0;
  let baseAmount = currentLoanBalance + secondLoanBalance + baseClosingCosts + prepaidExpenses;
  if (buttonType === 'onePayment') {
    baseAmount -= Number(loanAssumptions?.estimatedMonthlyPayment ?? 0);
  }
  const discountRebateRate = ((ratePrice - 100) / 100) * -1;
  return baseAmount / (1 - discountRebateRate);
};
